import { router } from '@inertiajs/react'
import { Page, PageHeader } from '@/components/page'
import { useState } from 'react'
import { DashboardLayout } from '@/components/layout/DashboardLayout'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Badge } from '@/components/ui/badge'
import { useBasePath } from '@/lib/paths'
import { X, Plus, Check, ChevronDown } from 'lucide-react'
import { cn } from '@/lib/utils'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command'

type Country = { id: string; code: string; name: string }

type Props = {
  countries: Country[]
}

export default function BusinessesNew({ countries }: Props) {
  const basePath = useBasePath()
  const [name, setName] = useState('')
  const [selectedCountryIds, setSelectedCountryIds] = useState<string[]>([])
  const [activities, setActivities] = useState<string[]>([])
  const [activityInput, setActivityInput] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [countriesOpen, setCountriesOpen] = useState(false)

  function toggleCountry(countryId: string) {
    setSelectedCountryIds(prev =>
      prev.includes(countryId)
        ? prev.filter(id => id !== countryId)
        : [...prev, countryId]
    )
  }

  function addActivity() {
    const trimmed = activityInput.trim()
    if (trimmed && !activities.includes(trimmed)) {
      setActivities(prev => [...prev, trimmed])
      setActivityInput('')
    }
  }

  function removeActivity(activity: string) {
    setActivities(prev => prev.filter(a => a !== activity))
  }

  function handleActivityKeyDown(e: React.KeyboardEvent) {
    if (e.key === 'Enter') {
      e.preventDefault()
      addActivity()
    }
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    setSubmitting(true)
    router.post(`${basePath}/businesses`, {
      business: {
        name,
        country_ids: selectedCountryIds,
        activities,
      }
    }, {
      onFinish: () => setSubmitting(false)
    })
  }

  const selectedCountries = countries.filter(c => selectedCountryIds.includes(c.id))

  return (
    <DashboardLayout
      title="New business"
      breadcrumbs={[{ label: 'Businesses', href: 'businesses' }, { label: 'New' }]}
    >
      <Page>
        <PageHeader
          title={<>New business</>}
          subtitle={<>Create a business to manage your services</>}
          actions={
            <form onSubmit={handleSubmit} className="space-y-5">
              <div className="space-y-2">
                <Label htmlFor="name">Business Name <span className="text-destructive">*</span></Label>
                <Input
                  id="name"
                  value={name}
                  onChange={e => setName(e.target.value)}
                  placeholder="e.g. Acme Corp"
                  required
                  autoFocus
                />
              </div>

              {/* Countries of Operation - Multiselect */}
              <div className="space-y-2">
                <Label>Countries of Operation</Label>
                <Popover open={countriesOpen} onOpenChange={setCountriesOpen}>
                  <PopoverTrigger asChild>
                    <button
                      type="button"
                      role="combobox"
                      aria-expanded={countriesOpen}
                      className={cn(
                        "border-input focus-visible:border-ring focus-visible:ring-ring/50 dark:bg-input/30 flex w-full items-center justify-between gap-2 rounded-md border bg-transparent px-3 py-1 text-sm shadow-xs transition-[color,box-shadow] outline-none focus-visible:ring-[3px] disabled:cursor-not-allowed disabled:opacity-50 h-9",
                        selectedCountryIds.length > 0 ? "text-foreground" : "text-muted-foreground"
                      )}
                    >
                      <span className="truncate">
                        {selectedCountryIds.length > 0
                          ? selectedCountries.map(c => c.name).join(', ')
                          : 'Select countries...'}
                      </span>
                      <ChevronDown className={cn("size-4 shrink-0 opacity-50 transition-transform", countriesOpen && "rotate-180")} />
                    </button>
                  </PopoverTrigger>
                  <PopoverContent className="w-[--radix-popover-trigger-width]! p-0" align="start">
                    <Command>
                      <CommandInput placeholder="Search countries..." />
                      <CommandList>
                        <CommandEmpty>No countries found.</CommandEmpty>
                        <CommandGroup>
                          {countries.map(country => (
                            <CommandItem
                              key={country.id}
                              value={country.name}
                              onSelect={() => toggleCountry(country.id)}
                              className="gap-2"
                            >
                              <Check className={cn("h-4 w-4 shrink-0", selectedCountryIds.includes(country.id) ? "opacity-100" : "opacity-0")} />
                              <span className="truncate">{country.name}</span>
                            </CommandItem>
                          ))}
                        </CommandGroup>
                      </CommandList>
                    </Command>
                  </PopoverContent>
                </Popover>
                {selectedCountries.length > 0 && (
                  <div className="flex flex-wrap gap-1.5 mt-2">
                    {selectedCountries.map(country => (
                      <Badge key={country.id} variant="secondary" className="gap-1 pr-1">
                        {country.name}
                        <button type="button" onClick={() => toggleCountry(country.id)} className="rounded-full p-0.5 hover:bg-muted-foreground/20">
                          <X className="h-3 w-3" />
                        </button>
                      </Badge>
                    ))}
                  </div>
                )}
              </div>

              {/* Business Activities */}
              <div className="space-y-2">
                <Label>Business Activities</Label>
                <div className="flex gap-2">
                  <Input
                    value={activityInput}
                    onChange={e => setActivityInput(e.target.value)}
                    onKeyDown={handleActivityKeyDown}
                    placeholder="e.g. Software Development"
                  />
                  <Button type="button" variant="outline" size="sm" onClick={addActivity} disabled={!activityInput.trim()}>
                    <Plus className="w-3.5 h-3.5" />
                  </Button>
                </div>
                {activities.length > 0 && (
                  <div className="flex flex-wrap gap-1.5 mt-2">
                    {activities.map(activity => (
                      <Badge key={activity} variant="secondary" className="gap-1 pr-1">
                        {activity}
                        <button type="button" onClick={() => removeActivity(activity)} className="rounded-full p-0.5 hover:bg-muted-foreground/20">
                          <X className="h-3 w-3" />
                        </button>
                      </Badge>
                    ))}
                  </div>
                )}
              </div>

              <div className="flex gap-2">
                <Button type="submit" size="sm" disabled={submitting}>
                  {submitting ? 'Creating...' : 'Create Business'}
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => router.visit(`${basePath}/businesses`)}
                >
                  Cancel
                </Button>
              </div>
            </form>
          }
        />

    
      </Page>
    </DashboardLayout>
  )
}
